import zoomSdk from "@zoom/appssdk";
import axios from 'axios';
import { endpoint } from '@repo/shared/utils/utils';

// Instead, use LaunchDarkly flags. These functions expect a flags object (from useFlags):
export const getLinkPrefix = (flags) => flags.linkPrefix || "/v2";

/**
 * Creates a configured Axios client instance with default headers and 401 handling
 * @param {Object} options - Configuration options
 * @param {string} [options.appContext] - Zoom app context for X-Zoom-App-Context header
 * @param {string} [options.sessionToken] - Session token for Authorization header
 * @param {Function} [options.on401] - Callback function to handle 401 responses
 * @param {number} [options.maxRetries=2] - Maximum number of retries for 401 responses
 * @returns {Object} Configured Axios instance
 */
export const get_api_client = ({ appContext, sessionToken, accountId, on401, maxRetries = 2 } = {}) => {
  // console.log('Getting API client with appContext:', appContext, 'sessionToken:', sessionToken, 'on401:', on401, 'maxRetries:', maxRetries);
  // Set default 401 handler if appContext is present and no custom handler provided
  const effectiveOn401 = on401 || (appContext ? () => handleZoomUnauthorized(appContext, accountId) : null);

  const client = axios.create({
    baseURL: `https://${endpoint()}`,
    headers: {
      'Content-Type': 'application/json',
      ...(appContext && { 'X-Zoom-App-Context': appContext }),
      ...(sessionToken && { 'X-PG-Auth': `Bearer ${sessionToken}` }),
      ...(accountId && { 'X-Zoom-Account-Id': accountId }),
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    }
  });

  client.interceptors.request.use(
    function (config) {
      // Do something before request is sent
      config.params = { timestamp: Date.now(), ...config.params || {} };
      return config;
    },
    function (error) {
      // Do something with request error
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle 401 errors
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Handle 401 Unauthorized responses
      if (error.response?.status === 401) {
        console.log('Caught 401 in axios interceptor');
        originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

        // Check if we've exceeded max retries
        if (originalRequest._retryCount > maxRetries) {
          console.error(`Max retry attempts (${maxRetries}) reached for request:`, originalRequest.url);
          return Promise.reject(error);
        }

        // Call the 401 handler if provided
        if (effectiveOn401) {
          try {
            await effectiveOn401();
            // Retry the original request
            return client(originalRequest);
          } catch (retryError) {
            return Promise.reject(retryError);
          }
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// In-app OAuth: state and the PKCE secret are both server-issued.
//
// The Zoom Apps SDK's authorize() only accepts `codeChallenge` (no
// `codeChallengeMethod`), so the SDK uses PKCE method "plain" -- under
// which the verifier sent to the token endpoint must equal the
// challenge sent at authorize.  The backend generates a single random
// secret and uses it as BOTH challenge (returned here) and verifier
// (kept in Redis under the state key); the frontend never sees a
// verifier and never chooses one.  The trust binding for the callback
// is the state, not the secrecy of the PKCE value.
//
// The onAuthorized event carries the redirectUri the Zoom client used
// for the authorization; the token exchange must repeat it, so it is
// posted with the code.
//
// One flow at a time: Zoom fires a single onAuthorized event no matter
// how many authorize() calls were made, so parallel flows would post the
// same code under different states and verifiers, and Zoom rejects all
// but one exchange with invalid_grant.
let inflightZoomReauth = null;

export const handleZoomUnauthorized = (appContext, accountId) => {
  if (inflightZoomReauth) {
    console.log('401 received, OAuth flow already in progress');
    return inflightZoomReauth;
  }
  inflightZoomReauth = runZoomReauth(appContext, accountId).finally(() => {
    inflightZoomReauth = null;
  });
  return inflightZoomReauth;
};

const runZoomReauth = async (appContext, accountId) => {
  console.log('401 received, triggering OAuth flow');

  const apiClient = get_api_client({ appContext, accountId });

  const { data: { state, codeChallenge } } = await apiClient.post(
    "/zoom-phone/install/initiate-in-app",
  );

  let authHandler;
  try {
    await new Promise((resolve, reject) => {
      authHandler = async (event) => {
        console.log("Sending token request to /zoom-phone/install (in-app path)");
        try {
          const response = await apiClient.post(
            `/zoom-phone/install?code=${encodeURIComponent(event.code)}`,
            { state, redirect_uri: event.redirectUri },
          );
          console.log("Token response status:", response.status);
          resolve();
        } catch (error) {
          console.error("Zoom Phone Install failed:", error.response?.status || error.message, error);
          reject(error);
        }
      };

      zoomSdk.addEventListener("onAuthorized", authHandler);

      Promise.resolve()
        .then(() => zoomSdk.authorize({ codeChallenge, state }))
        .catch((error) => {
          console.error("Zoom SDK Error:", error);
          reject(error);
        });
    });
  } finally {
    if (authHandler) {
      zoomSdk.removeEventListener("onAuthorized", authHandler);
    }
  }
};


// const crypto = require("crypto-browserify");

//function to get the meeting number and passwork from the url
export function getMeetingNumberAndPasswordFromUrl(url) {
  const splitUrl = url.split('?')[0];

  if (!splitUrl) {
    return {
      meetingNumber: null,
      password: null
    };
  }

  const meetingNumber = splitUrl.substring(splitUrl.lastIndexOf('/') + 1);
  const queryString = url.split('?')[1];
  const password = queryString ? queryString.split('pwd=')[1] : null;

  return {
    meetingNumber,
    password
  };
}


export { endpoint };

export const envLabel = () => {
  const url = new URL(window.location.href);
  const hostname = url.hostname;
  if (hostname.includes(STAGING_HOSTNAMES)) {
    return { label: "STAGING", color: "brown", env: "staging" };
  } else if (hostname.includes(DEV_HOSTNAMES)) {
    return { label: "LOCAL DEV", color: "orange", env: "local" };
  }
  return { label: "", color: "green", env: "prod" };
}


export const fallbackCopyTextToClipboard = (text) => {
  var textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    var successful = document.execCommand('copy');
    var msg = successful ? 'successful' : 'unsuccessful';
    console.log('Fallback: Copying text command was ' + msg);
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err);
  }

  document.body.removeChild(textArea);
}

export const generateMeetingReport = async (meetingId, meetingUuid, appContext, accountId, polyUserId) => {
  try {
    const apiClient = get_api_client({ appContext, accountId });
    const response = await apiClient.post('/v2/reports/meeting', {
      meeting_id: meetingId,
      meeting_uuid: meetingUuid,
      poly_user_id: polyUserId
    });

    const reportData = response.data;

    if (!reportData.url) {
      throw new Error('No download URL returned');
    }

    return reportData;
  } catch (error) {
    console.error('Error generating meeting report:', error);
    throw error;
  }
};

export const fetchMeetingDetails = async (polyUserId, meetingId, meetingUuid, appContext, accountId, startTime) => {
  try {
    const apiClient = get_api_client({ appContext, accountId });
    const response = await apiClient.post('/v2/logs/meeting/details', {
      meeting_id: meetingId,
      meeting_uuid: meetingUuid,
      start_time: startTime
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching meeting details:', error);
    throw error;
  }
};

export const maybeAddZoomAccountId = async (sessionToken, accountId, appContext) => {
  try {
    console.log('Adding zoom account ID:', accountId);
    const apiClient = get_api_client({ sessionToken, appContext, accountId });
    const response = await apiClient.post('/zoom-phone/zoom-account/', {
      zoom_account_id: accountId,
    });

    const result = response.data || { message: 'Request completed' };
    console.log('Add Zoom account operation:', result.message);
  } catch (error) {
    console.error('Error adding zoom account ID:', error);
  }
};

export const createAccount = async (accountName) => {
  if (!accountName || !accountName.trim()) {
    throw new Error('Account name is required');
  }

  if (!sessionToken) {
    throw new Error('Session token is required');
  }

  try {
    const apiClient = get_api_client({ sessionToken });
    const response = await apiClient.post('/create-account/', {
      name: accountName.trim()
    });
    return response.data;
  } catch (error) {
    const errorData = error.response?.data || { detail: 'Failed to create account' };
    throw new Error(errorData.detail || 'Failed to create account');
  }
};

export const portalLogin = async (userBundle) => {
  if (!userBundle) {
    throw new Error('User bundle is required');
  }

  const apiClient = get_api_client();
  const response = await apiClient.post('/portal_login', {
    user_bundle: userBundle
  });
  return response.data.jwt;
};


/**
 * Opens a URL using zoomSdk if available, otherwise falls back to window.open
 * @param {string} url - The URL to open
 * @param {string} [target='_blank'] - The target for window.open (only used if zoomSdk is not available)
 * @returns {Promise<void>}
 */
export const openURL = async (url, target = '_blank') => {
  try {
    if (zoomSdk && typeof zoomSdk.openUrl === 'function') {
      await zoomSdk.openUrl({ url });
    } else {
      window.open(url, target, 'noopener,noreferrer');
    }
  } catch (error) {
    window.open(url, target, 'noopener,noreferrer');
  }
};

